import React from "react";
import { Button, Card } from "react-bootstrap";
import "./LocationDetailsCard.css";

import {
    DtoIncomingNotification,
    DtoLocationDetailsCardProps,
} from "../../core/dto/dashboard.dto";
import { useAppDispatch, useAppSelector } from "../../core/redux/hooks";
import { terminateVoiceCall, setOutBoundCallLocationDetails, terminateOutBoundVoiceCall } from "../../core/redux/notificationsSlice";
import { PhoneNumberFormatter, RemoveExtraCharsFromNumber, checkCallSid, loggerHelper } from "../../core/services/sharedService";
import { PostApiMethod } from "../../core/services/apiService";

const LocationDetailsCard = ({
    locationInformation,
    notificationData,
    acceptVoiceCall,
    terminateVideoCall,
    restartMotionDetection,
    sendMessage,
    stopNotificationRing,
    localParticipantDetails,
    agentStatus,
    inCallRef,
    onlineLocations,
    getUserMediaDetails,
    incomingNotificationData,
    putCallOnHold,
    resumeVideoCall,
    showDndModal,
    dndTime,
    callTriggeredFlag,
    setCallTriggeredFlag,
    currentAgentId
}: DtoLocationDetailsCardProps) => {
    const dispatch = useAppDispatch();
    const voice_call_type = 'voice';
    const video_call_type = 'video';
    let [notificationDate, setNotificationDate] = React.useState<string>("");
    let [cardClassName, setCardClassName] = React.useState<string>("");
    let [addAnimation, setAddAnimation] = React.useState<boolean>(false);
    let [isLoading, setIsLoading] = React.useState<boolean>(false);
    let [isVideoCall, setIsVideoCall] = React.useState<boolean>(false);
    let [isLocalParticipantExist, setLocalParticipantExist] = React.useState<boolean>(false);
    let [muteRinger, setMuteRinger] = React.useState(false);
    let notificationsSliceSelector = useAppSelector(
        (state: any) => state.agentNotification
    );
    // Custom PMS ('other') notes, shown under the PMS name. Notes on any other provider are stale.
    const customPmsNotes = locationInformation?.pms_provider === "other" && typeof locationInformation?.pms_notes === "string"
        ? locationInformation.pms_notes.trim()
        : "";
    const isCallInHold = React.useRef<boolean>(false);
    const uniqueIdRef = React.useRef<string>();
    const inCallState: boolean =
        (notificationData &&
            "callAccepted" in notificationData &&
            notificationData.callAccepted === true) ||
        (locationInformation &&
            (locationInformation._id === inCallRef ||
                (notificationsSliceSelector.holdCallDetails &&
                    notificationsSliceSelector.holdCallDetails.inCallRefId ===
                    locationInformation._id)) &&
            !notificationData);

    React.useEffect(() => {
        const uniqueId: string =
            notificationData && notificationData.notificationId
                ? notificationData.notificationId
                : locationInformation._id;
        uniqueIdRef.current = uniqueId;
        isCallInHold.current =
            notificationsSliceSelector.holdCallDetails &&
                notificationsSliceSelector.holdCallDetails.inCallRefId &&
                notificationsSliceSelector.holdCallDetails.inCallRefId === uniqueId
                ? true
                : false;
        if (inCallState && !isCallInHold.current) {
            setCardClassName(" inCallCards ");
            setAddAnimation(false);
            // setInCallState(true);
        } else if (notificationData && !isCallInHold.current) {
            setCardClassName(" notificationCards ");
            setAddAnimation(true);
        } else if (isCallInHold.current) {
            setCardClassName(" onHoldCards ");
            setAddAnimation(false);
        } else {
            setCardClassName(" ");
            setAddAnimation(false);
        }
        if (notificationData && notificationData.time) {
            const dateValue: Date = new Date(notificationData.time);
            const formattedTime: string =
                ("00" + (dateValue.getMonth() + 1)).slice(-2) +
                "/" +
                ("00" + dateValue.getDate()).slice(-2) +
                "/" +
                dateValue.getFullYear() +
                " " +
                ("00" + dateValue.getHours()).slice(-2) +
                ":" +
                ("00" + dateValue.getMinutes()).slice(-2) +
                ":" +
                ("00" + dateValue.getSeconds()).slice(-2);
            setNotificationDate(formattedTime);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        inCallState,
        locationInformation,
        notificationData,
        isCallInHold.current,
        notificationsSliceSelector.holdCallDetails,
    ]);

    React.useEffect(() => {
        if (localParticipantDetails) {
            setIsLoading(false);
            setLocalParticipantExist(true);
        } else {
            setLocalParticipantExist(false);
        }
    }, [localParticipantDetails]);

    const joinCallFn = React.useCallback(
        async (notificationData: DtoIncomingNotification) => {
            // loggerHelper("Agent dashboard locationDetails joinCallFn notificationData", { notificationData: notificationData, currentAgentId: currentAgentId });
            loggerHelper("Agent dashboard locationDetails joinCallFn notificationData", { currentAgentId: currentAgentId, call_sid: notificationData?.call_sid });
            dispatch(setOutBoundCallLocationDetails(undefined));
            const permissionEnabled = await getUserMediaDetails();
            if (!permissionEnabled) {
                return;
            }
            if (notificationData && notificationData.call_type !== voice_call_type && !notificationData.conference_call) {
                sendMessage(notificationData, video_call_type);
                setIsLoading(true);
                stopNotificationRing("join");
                setIsVideoCall(true);
            } else if (notificationData && notificationData.call_type !== voice_call_type && notificationData.conference_call) {
                sendMessage(notificationData, video_call_type, notificationData.conference_call, notificationData.roomName, notificationData.room_sid);
                setIsLoading(true);
                stopNotificationRing("join");
                setIsVideoCall(true);
            }
            else if (
                notificationData &&
                notificationData.call_type === voice_call_type &&
                acceptVoiceCall
            ) {
                loggerHelper("Before dispatching acceptVoiceCall", { currentAgentId: currentAgentId, call_sid: notificationData?.call_sid });
                dispatch(acceptVoiceCall(checkCallSid(notificationData?.call_sid)));
                stopNotificationRing('join');
                setIsVideoCall(false);
                setCallTriggeredFlag(false);
            } else {
                const callInfo: DtoIncomingNotification = {
                    locationId: locationInformation?._id,
                };
                sendMessage(callInfo, video_call_type);
                setIsLoading(true);
                setIsVideoCall(true);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            acceptVoiceCall,
            getUserMediaDetails,
            locationInformation,
            sendMessage,
            stopNotificationRing,
            dispatch
        ]
    );

    const checkIncomingVoiceCall = React.useCallback(() => {
        if (incomingNotificationData) {
            if (incomingNotificationData.filter((notificationInstance: any) => notificationInstance.call_type && notificationInstance.call_type === voice_call_type).length === 1) {
                if (notificationsSliceSelector.holdCallDetails) {
                    return false;
                }
                return true;
            } else if (incomingNotificationData.filter((notificationInstance: any) => notificationInstance.call_type && notificationInstance.call_type === voice_call_type).length > 1) {
                return true;
            }
        }
        return false;
    }, [incomingNotificationData, notificationsSliceSelector]);

    const joinOutBoundVoiceCallCheck = React.useCallback(async () => {
        const permissionEnabled = await getUserMediaDetails();
        if (!permissionEnabled) {
            loggerHelper("inside permissionEnabled line 192", "");
            return;
        }
        dispatch(setOutBoundCallLocationDetails(locationInformation));
    }, [dispatch, getUserMediaDetails, locationInformation]);

    // check's for call hold or resume functionality
    const callHoldCheck = React.useCallback((callType: string | undefined) => {
        if (callType) {
            if (isCallInHold.current) {
                resumeVideoCall(callType);
            } else {
                putCallOnHold(callType);
            }
        } else {
            let callTypeCheck = ((notificationsSliceSelector.holdCallDetails && notificationsSliceSelector.holdCallDetails.token) || (!notificationsSliceSelector.callDetails && !notificationsSliceSelector.outBoundCallConnection)) ? video_call_type : voice_call_type;
            if (isCallInHold.current) {
                resumeVideoCall(callTypeCheck);
            } else {
                putCallOnHold(callTypeCheck);
            }
        }
    }, [notificationsSliceSelector, putCallOnHold, resumeVideoCall]);


    return (
        <>
            {(notificationData && locationInformation) ?
                <Card
                    className={"card mb-2 " + cardClassName}
                    key={
                        notificationData?.locationId
                            ? notificationData?.locationId
                            : locationInformation._id
                    }
                >
                    <Card.Body className="cardbody">
                        {
                            <div className="location-details row d-flex align-items-center">
                                <div
                                    className={
                                        notificationData
                                            ? "col-md-9"
                                            : "col-md-12"
                                    }
                                >
                                    <div>
                                        <label className="d-inline-flex">
                                            {onlineLocations.online.includes(
                                                locationInformation._id
                                            ) ? (
                                                <span className="location-card-online">
                                                    {" "}
                                                </span>
                                            ) : onlineLocations.notified.includes(
                                                locationInformation._id
                                            ) ? (
                                                <span className="location-card-notified">
                                                    {" "}
                                                </span>
                                            ) : onlineLocations.busy.includes(
                                                locationInformation._id
                                            ) ? (
                                                <span className="location-card-busy">
                                                    {" "}
                                                </span>
                                            ) : onlineLocations.dnd.includes(
                                                locationInformation._id
                                            ) ? (
                                                <span className="location-card-dnd">
                                                    {" "}
                                                </span>
                                            ) : (
                                                <span className="location-card-offline">
                                                    {" "}
                                                </span>
                                            )}
                                            <h4 className="ml-2 text-uppercase">
                                                {locationInformation.name}
                                            </h4>
                                            {
                                                isCallInHold.current && <span className="ml-2 mt-1 text-danger">
                                                    <i className="bi bi-pause-circle-fill mr-1" />
                                                    <b>On hold</b>
                                                </span>
                                            }
                                        </label>
                                    </div>
                                    <div className="text-small">
                                        {locationInformation.address} , {" "}
                                        Contact:{locationInformation.customer_contact ? (PhoneNumberFormatter(RemoveExtraCharsFromNumber(locationInformation.customer_contact?.toString()).slice(1), RemoveExtraCharsFromNumber(locationInformation.customer_contact?.toString()).slice(1).length)) : " Not available"}
                                        <br />
                                        {locationInformation.city} ,{" "}
                                        {locationInformation.state}{locationInformation.zipCode ? ", " + locationInformation.zipCode : ""}
                                        {" PMS :"}
                                        <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                            {locationInformation?.pms_name ?? " Not Available"}
                                        </span>
                                        {customPmsNotes && (
                                            <div style={{ whiteSpace: 'pre-wrap' }}>PMS Notes : {customPmsNotes}</div>
                                        )}
                                    </div>
                                    {notificationDate && (
                                        <div className="text-small mt-1">
                                            {notificationDate}
                                        </div>
                                    )}
                                    <div className="dashboard-info-text mt-1">
                                        <div>
                                            {locationInformation.primary_agent &&
                                                locationInformation.primary_agent
                                                    .name ? (
                                                <div className="d-flex flex-wrap justify-content-start">
                                                    <p className="label-info">
                                                        <i className="bi bi-person-fill"></i> Primary agent:
                                                    </p>
                                                    <div className='status-circle'>
                                                        <span
                                                            className={
                                                                locationInformation
                                                                    .primary_agent
                                                                    .email &&
                                                                    agentStatus[
                                                                    locationInformation
                                                                        .primary_agent
                                                                        .email
                                                                    ] &&
                                                                    agentStatus[
                                                                        locationInformation
                                                                            .primary_agent
                                                                            .email
                                                                    ].status
                                                                    ? agentStatus[
                                                                        locationInformation
                                                                            .primary_agent
                                                                            .email
                                                                    ].status +
                                                                    " mx-2"
                                                                    : "offline mx-2"
                                                            }
                                                        ></span>
                                                    </div>
                                                    <div className="ml-2">
                                                        <p className="label-info">
                                                            <span>
                                                                {
                                                                    locationInformation
                                                                        .primary_agent
                                                                        .name
                                                                }
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                        <div>
                                            {locationInformation.secondary_agent &&
                                                locationInformation.secondary_agent
                                                    .name ? (
                                                <div className="d-flex flex-wrap justify-content-start">
                                                    <div>
                                                        <p className="label-info">
                                                            <i className="bi bi-person-fill"></i> Secondary agent:
                                                        </p>
                                                    </div>
                                                    <div className='status-circle'>
                                                        <span
                                                            className={
                                                                locationInformation
                                                                    .secondary_agent
                                                                    .email &&
                                                                    agentStatus[
                                                                    locationInformation
                                                                        .secondary_agent
                                                                        .email
                                                                    ] &&
                                                                    agentStatus[
                                                                        locationInformation
                                                                            .secondary_agent
                                                                            .email
                                                                    ].status
                                                                    ? agentStatus[
                                                                        locationInformation
                                                                            .secondary_agent
                                                                            .email
                                                                    ].status +
                                                                    " mx-2"
                                                                    : "offline mx-2"
                                                            }
                                                        ></span>
                                                    </div>

                                                    <div className="ml-2">
                                                        <p className="label-info">
                                                            <span>
                                                                {
                                                                    locationInformation
                                                                        .secondary_agent
                                                                        .name
                                                                }
                                                            </span>
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    </div>
                                    <div className="button-group-info">
                                        <Button
                                            size="sm"
                                            className={
                                                addAnimation
                                                    ? (notificationData.call_type === video_call_type ? "videoCallBtnColor " : "btn btn-success ") + "button notification-button-standards incoming-call-animation"
                                                    : (notificationData.call_type === video_call_type ? "videoCallBtnColor " : "btn btn-success ") + "button notification-button-standards"
                                            }
                                            onClick={(event) => {
                                                joinCallFn(notificationData!);
                                                setCallTriggeredFlag(true);
                                            }}
                                            disabled={
                                                (
                                                    localParticipantDetails
                                                    &&
                                                    notificationData &&
                                                    notificationData.call_type ===
                                                    video_call_type
                                                )
                                                    ||
                                                    isLoading ||
                                                    (notificationData && notificationData.call_type === video_call_type &&
                                                        onlineLocations.offline.includes(
                                                            locationInformation._id
                                                        ) && !notificationData.tenant_triggered) ||
                                                    inCallState ||
                                                    inCallRef
                                                    ? true
                                                    : false
                                                    || callTriggeredFlag
                                            }
                                        >
                                            {
                                                notificationData ?
                                                    (
                                                        notificationData.call_type ===
                                                            voice_call_type ? (
                                                            <i className="bi bi-telephone-fill"></i>
                                                        ) : (
                                                            notificationData.call_type === video_call_type && notificationData.callCategory === "qrScan" ?
                                                                <i className="bi bi-qr-code"></i> :
                                                                notificationData.call_type === video_call_type && notificationData.callCategory === "website" ?
                                                                    <i className="bi bi-browser-chrome"></i> :
                                                                    notificationData.call_type === video_call_type && !notificationData.callCategory ?
                                                                        <i className="bi bi-camera-video-fill"></i> :
                                                                        <i className="bi bi-camera-video-fill"></i>
                                                        )
                                                    ) : (
                                                        <></>
                                                    )}
                                            &nbsp; ANSWER CALL
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="btn btn-danger button button-hangup-incoming notification-button-standards"
                                            id={
                                                notificationData &&
                                                    notificationData.call_type !==
                                                    voice_call_type
                                                    ? ""
                                                    : "button-hangup-incoming"
                                            }
                                            disabled={isLoading || !inCallState || isCallInHold.current || (isVideoCall && !isLocalParticipantExist)}
                                            onClick={(e) => {
                                                if (
                                                    notificationData?.call_type !==
                                                    voice_call_type
                                                ) {
                                                    terminateVideoCall(
                                                        notificationData &&
                                                            notificationData.notificationId
                                                            ? notificationData.notificationId
                                                            : locationInformation._id,
                                                        false,
                                                        notificationData &&
                                                            notificationData.notificationId
                                                            ? "no"
                                                            : "yes"
                                                    );
                                                }
                                                if (
                                                    notificationData?.call_type ===
                                                    voice_call_type
                                                ) {
                                                    dispatch(
                                                        terminateVoiceCall({
                                                            callerId:
                                                                notificationData?.call_sid!,
                                                            disconnectCall:
                                                                true,
                                                        })
                                                    );
                                                }
                                                if (notificationData?.call_type !== voice_call_type && notificationData?.call_type !== video_call_type) {
                                                    dispatch(terminateOutBoundVoiceCall());
                                                }
                                            }}
                                        >
                                            END CALL
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="btn btn-secondary button"
                                            disabled={
                                                isLoading ||
                                                (notificationData && notificationData.call_type === video_call_type && notificationData.room_name !== undefined && notificationData.room_name !== null) ||
                                                (notificationData &&
                                                    notificationData.call_type ===
                                                    voice_call_type) ||
                                                (notificationData && notificationData.call_type === video_call_type &&
                                                    onlineLocations.offline.includes(
                                                        locationInformation._id
                                                    )) ||
                                                inCallState || notificationData.conference_call === true
                                            }
                                            onClick={(e) => {
                                                notificationData
                                                    ? restartMotionDetection(
                                                        locationInformation._id,
                                                        notificationData.notificationId
                                                    )
                                                    : restartMotionDetection(
                                                        locationInformation._id
                                                    );
                                                stopNotificationRing("reset");
                                            }}
                                        >
                                            KIOSK - RESTART MOTION
                                        </Button>
                                        <Button
                                            size="sm"
                                            className="btn btn-success button notification-button-standards"
                                            disabled={inCallRef ? true : false || checkIncomingVoiceCall()}
                                            onClick={(e) => { joinOutBoundVoiceCallCheck(); }}
                                        >
                                            <i className="bi bi-telephone-outbound-fill" /> &nbsp; VOICE CALL
                                        </Button>
                                        {
                                            !isLoading &&
                                                inCallState &&
                                                (!notificationsSliceSelector.holdCallDetails ||
                                                    (notificationsSliceSelector.holdCallDetails &&
                                                        notificationsSliceSelector.holdCallDetails.inCallRefId &&
                                                        uniqueIdRef.current ===
                                                        notificationsSliceSelector.holdCallDetails.inCallRefId)) ? (
                                                <Button
                                                    size="sm"
                                                    className="btn btn-warning button notification-button-standards"
                                                    onClick={() => callHoldCheck(notificationData.call_type)}
                                                    disabled={(isCallInHold.current) ? (inCallRef) ? true : false : (inCallRef === uniqueIdRef.current) ? false : true}
                                                >
                                                    {isCallInHold.current ? (
                                                        <i className="bi bi-play-circle-fill" />
                                                    ) : (
                                                        <i className="bi bi-pause-circle-fill" />
                                                    )}{" "}
                                                    &nbsp;
                                                    {isCallInHold.current ? "Resume" : "Hold"}
                                                </Button>
                                            ) : (
                                                <></>
                                            )
                                        }
                                        {
                                            (notificationData && notificationData.call_type !== voice_call_type) &&
                                            <Button
                                                size="sm"
                                                className="btn btn-warning button"
                                                onClick={(e) => {
                                                    showDndModal(notificationData?.locationId
                                                        ? notificationData?.locationId
                                                        : locationInformation._id, notificationData.notificationId);
                                                    stopNotificationRing("reset");
                                                }}
                                                disabled={isLoading || (notificationData && notificationData.call_type === video_call_type && notificationData.room_name !== undefined && notificationData.room_name !== null) || (onlineLocations.dnd.includes(locationInformation._id)) || (notificationData && notificationData.call_type === voice_call_type) || (notificationData && onlineLocations.offline.includes(locationInformation._id)) || inCallState || notificationData.conference_call === true
                                                }
                                            >
                                                <i className="fa fa-ban" aria-hidden="true"></i>{" "} KIOSK - DO NOT DISTURB
                                            </Button>
                                        }

                                        <Button
                                            size="sm"
                                            className="btn btn-dark button notification-button-standards"
                                            onClick={(e) => { stopNotificationRing("mute-ringer"); setMuteRinger(true) }}
                                            disabled={(muteRinger) ||
                                                (notificationData &&
                                                    onlineLocations.busy.includes(
                                                        locationInformation._id
                                                    ) && !notificationData.conference_call) || (notificationsSliceSelector.inHandOffCall === locationInformation._id || inCallState)
                                                ? true
                                                : false}
                                        >
                                            <i className="bi bi-bell-slash-fill" style={{ fontSize: '15px' }} /> &nbsp; MUTE
                                        </Button>

                                    </div>
                                </div>
                                {notificationData &&
                                    notificationData.screenshot && (
                                        <div className="col-md-3">
                                            <img
                                                src={
                                                    notificationData
                                                        ? notificationData.screenshot
                                                        : locationInformation.placeholder
                                                }
                                                className="imageLogo"
                                                width="100%"
                                                height="auto"
                                                alt="temp"
                                            />
                                        </div>
                                    )}
                                {
                                    notificationData &&
                                    notificationData.conference_call && notificationData.invited_by?.email && (
                                        <div className="col-md-3 mx-auto text-center device-info">
                                            <span><i className="bi bi-people" style={{ fontSize: '45px' }}></i></span><br />
                                            {/* <span><i className="fa fa-group" style={{ fontSize: '70px' }}></i></span><br /> 
                                            <span>{notificationData.invited_by?.name}</span><br />
                                            <span>{notificationData.invited_by?.email}</span> */}
                                            <span>Incoming Call from {notificationData.invited_by?.name} - 3rd Person Call</span>
                                        </div>
                                    )
                                }
                                {
                                    notificationData &&
                                    (notificationData.call_type === video_call_type && notificationData.callCategory) && (
                                        notificationData.callCategory === "qrScan" ?
                                            <div className="col-md-3 mx-auto text-center device-info">
                                                <i className="bi bi-qr-code" style={{ fontSize: '45px' }}></i>
                                                <p><b>QR Code - Video Call</b></p>
                                            </div> :
                                            <div className="col-md-3 mx-auto text-center device-info">
                                                <i className="bi bi-browser-chrome" style={{ fontSize: '45px' }}></i>
                                                <p><b>Website - Video call</b></p>
                                            </div>
                                    )
                                }
                                {
                                    notificationData &&
                                        notificationData.call_type &&
                                        notificationData.call_type === voice_call_type ? (
                                        <div className="col-md-3 addressInfo">
                                            <i className={(addAnimation) ? "bi bi-telephone-inbound-fill with-incoming-call-animation" : "bi bi-telephone-inbound-fill"}></i>

                                            <div>
                                                <i className={(addAnimation) ? "icon-lg bi bi-telephone-fill with-incoming-call-animation" : "icon-lg bi bi-telephone-fill"}></i>
                                                <span className="text-small">
                                                    +
                                                    {notificationData.contact_from
                                                        ? notificationData.contact_from
                                                        : ""}
                                                </span>
                                            </div>

                                            <div>
                                                <i className="bi bi-geo-alt-fill"></i>
                                                <span className="text-small">
                                                    {notificationData.caller_city
                                                        ? notificationData.caller_city +
                                                        ", "
                                                        : ""}{" "}
                                                    {notificationData.caller_state
                                                        ? notificationData.caller_state +
                                                        ", "
                                                        : ""}{" "}
                                                    {notificationData.caller_Country
                                                        ? notificationData.caller_Country
                                                        : ""}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <></>
                                    )
                                }
                            </div >
                        }
                    </Card.Body >
                </Card >
                :
                (!notificationData && locationInformation) ?
                    <Card
                        className={"card mb-2 " + cardClassName}
                        key={locationInformation._id}
                    >
                        <Card.Body className="cardbody">
                            {
                                <div className="location-details row d-flex align-items-center">
                                    <div className={"col-md-12"}>
                                        <div>
                                            <label className="d-inline-flex">
                                                {onlineLocations.online.includes(
                                                    locationInformation._id
                                                ) ? (
                                                    <span className="location-card-online">
                                                        {" "}
                                                    </span>
                                                ) : onlineLocations.notified.includes(
                                                    locationInformation._id
                                                ) ? (
                                                    <span className="location-card-notified">
                                                        {" "}
                                                    </span>
                                                ) : onlineLocations.busy.includes(
                                                    locationInformation._id
                                                ) ? (
                                                    <span className="location-card-busy">
                                                        {" "}
                                                    </span>
                                                ) : onlineLocations.dnd.includes(
                                                    locationInformation._id
                                                ) ? (
                                                    <span className="location-card-dnd">
                                                        {" "}
                                                    </span>
                                                ) : (
                                                    <span className="location-card-offline">
                                                        {" "}
                                                    </span>
                                                )}
                                                <h4 className="ml-2 text-uppercase">
                                                    {locationInformation.name}
                                                </h4>
                                                {
                                                    isCallInHold.current && <span className="ml-2 mt-1 text-danger">
                                                        <i className="bi bi-pause-circle-fill mr-1" />
                                                        <b>On hold</b>
                                                    </span>
                                                }
                                            </label>
                                        </div>
                                        <div>
                                            {(dndTime) &&
                                                onlineLocations.dnd.includes(locationInformation._id) ?
                                                <>
                                                    <span className="fa fa-ban text-danger" style={{ fontSize: "20px" }}>
                                                        <span className="ml-2 text-danger" style={{ fontSize: "16px" }}>
                                                            In do not disturb mode until: {" "}
                                                            {
                                                                onlineLocations.dnd.includes(locationInformation._id) ? <>
                                                                    {(
                                                                        "0" +
                                                                        Math.floor((dndTime[locationInformation._id] / 60000) % 60)
                                                                    ).slice(-2)}
                                                                    :
                                                                    {(
                                                                        "0" +
                                                                        Math.floor((dndTime[locationInformation._id] / 1000) % 60)
                                                                    ).slice(-2)} </> : ""
                                                            }
                                                        </span>
                                                    </span>
                                                </>
                                                : ""
                                            }
                                        </div>
                                        <div className="text-small">
                                            {locationInformation.address} , {" "}
                                            Contact : {locationInformation.customer_contact ? (PhoneNumberFormatter(RemoveExtraCharsFromNumber(locationInformation.customer_contact?.toString()).slice(1), RemoveExtraCharsFromNumber(locationInformation.customer_contact?.toString()).slice(1).length)) : "Not available"}<br />
                                            {locationInformation.city} ,{" "}
                                            {locationInformation.state}{locationInformation.zipCode ? ", " + locationInformation.zipCode : ""}
                                            {", PMS : "}
                                            <span style={{ fontWeight: 'bold', textDecoration: 'underline' }}>
                                                {locationInformation?.pms_name ?? " Not Available"}
                                            </span>
                                            {customPmsNotes && (
                                                <div style={{ whiteSpace: 'pre-wrap' }}>PMS Notes : {customPmsNotes}</div>
                                            )}

                                        </div>
                                        {notificationDate && (
                                            <div className="text-small mt-1">
                                                {notificationDate}
                                            </div>
                                        )}
                                        <div className="dashboard-info-text mt-1">
                                            <div>
                                                {locationInformation.primary_agent &&
                                                    locationInformation.primary_agent
                                                        .name ? (
                                                    <div className="d-flex flex-wrap justify-content-start">

                                                        <p className="label-title">
                                                            <i className="bi bi-person-fill"></i>   Primary agent:
                                                        </p>

                                                        <div className='status-circle'>
                                                            <span
                                                                className={
                                                                    locationInformation
                                                                        .primary_agent
                                                                        .email &&
                                                                        agentStatus[
                                                                        locationInformation
                                                                            .primary_agent
                                                                            .email
                                                                        ] &&
                                                                        agentStatus[
                                                                            locationInformation
                                                                                .primary_agent
                                                                                .email
                                                                        ].status
                                                                        ? agentStatus[
                                                                            locationInformation
                                                                                .primary_agent
                                                                                .email
                                                                        ].status +
                                                                        " mx-2"
                                                                        : "offline mx-2"
                                                                }
                                                            ></span>
                                                        </div>
                                                        <p className="label-info">
                                                            <span className="ml-2">
                                                                {
                                                                    locationInformation
                                                                        .primary_agent
                                                                        .name
                                                                }
                                                            </span>
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                            <div>
                                                {locationInformation.secondary_agent &&
                                                    locationInformation.secondary_agent
                                                        .name ? (
                                                    <div className="d-flex flex-wrap justify-content-start">
                                                        <p className="label-title">
                                                            <i className="bi bi-person-fill"></i> Secondary agent:
                                                        </p>
                                                        <div className='status-circle'>
                                                            <span
                                                                className={
                                                                    locationInformation
                                                                        .secondary_agent
                                                                        .email &&
                                                                        agentStatus[
                                                                        locationInformation
                                                                            .secondary_agent
                                                                            .email
                                                                        ] &&
                                                                        agentStatus[
                                                                            locationInformation
                                                                                .secondary_agent
                                                                                .email
                                                                        ].status
                                                                        ? agentStatus[
                                                                            locationInformation
                                                                                .secondary_agent
                                                                                .email
                                                                        ].status +
                                                                        " mx-2"
                                                                        : "offline mx-2"
                                                                }
                                                            ></span>
                                                        </div>

                                                        <p className="label-info">
                                                            <span className="ml-2">
                                                                {
                                                                    locationInformation
                                                                        .secondary_agent
                                                                        .name
                                                                }
                                                            </span>
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <></>
                                                )}
                                            </div>
                                        </div>
                                        <div className="button-group-info">
                                            <Button
                                                size="sm"
                                                style={{ backgroundColor: "#00517d" }}
                                                className={
                                                    addAnimation
                                                        ? "button notification-button-standards incoming-call-animation "
                                                        : "button notification-button-standards"
                                                }
                                                onClick={(event) => {
                                                    joinCallFn(notificationData!);
                                                    setCallTriggeredFlag(true);
                                                }}
                                                disabled={isLoading || (!notificationData && !onlineLocations.online.includes(locationInformation._id)) || inCallState || (inCallRef) || isCallInHold.current ? true : false || callTriggeredFlag}
                                            >
                                                <i className="bi bi-camera-video-fill"></i>
                                                &nbsp; VIDEO CALL
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="btn btn-danger button button-hangup-incoming notification-button-standards"
                                                disabled={isLoading || !inCallState || (isVideoCall && !isLocalParticipantExist) || isCallInHold.current}
                                                onClick={(e) => {
                                                    if (inCallRef && inCallRef === locationInformation._id) {
                                                        terminateVideoCall(locationInformation._id, false, "yes");
                                                        dispatch(terminateOutBoundVoiceCall());
                                                    }
                                                }}
                                            >
                                                END CALL
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="btn btn-secondary button"
                                                disabled={notificationsSliceSelector.restartMotionStateLocationId.includes(locationInformation._id) || isLoading || (!notificationData && !onlineLocations.online.includes(locationInformation._id) && !onlineLocations.dnd.includes(locationInformation._id)) || inCallState}
                                                onClick={(e) => { restartMotionDetection(locationInformation._id); stopNotificationRing("reset"); }}
                                            >
                                                {(notificationsSliceSelector.restartMotionStateLocationId.includes(locationInformation._id)) &&
                                                    (onlineLocations.online.includes(locationInformation._id)) ?
                                                    <>
                                                        <span className="spinner-border text-white mr-1" style={{ width: '12px', height: "12px" }} role="status"></span>
                                                    </>
                                                    : ""
                                                }
                                                KIOSK - RESTART MOTION
                                            </Button>
                                            <Button
                                                size="sm"
                                                className="btn btn-success button notification-button-standards"
                                                disabled={inCallRef ? true : false || checkIncomingVoiceCall() || isCallInHold.current}
                                                onClick={(e) => { joinOutBoundVoiceCallCheck(); }}
                                            >
                                                <i className="bi bi-telephone-outbound-fill" /> &nbsp; VOICE CALL
                                            </Button>
                                            {
                                                !isLoading &&
                                                    inCallState &&
                                                    (!notificationsSliceSelector.holdCallDetails ||
                                                        (notificationsSliceSelector.holdCallDetails &&
                                                            notificationsSliceSelector.holdCallDetails.inCallRefId &&
                                                            uniqueIdRef.current ===
                                                            notificationsSliceSelector.holdCallDetails.inCallRefId)) ? (
                                                    <Button
                                                        size="sm"
                                                        className="btn btn-warning button notification-button-standards"
                                                        onClick={() => callHoldCheck(undefined)}
                                                        disabled={(isCallInHold.current) ? (inCallRef) ? true : false : (inCallRef === uniqueIdRef.current) ? false : true}
                                                    >
                                                        {isCallInHold.current ? (
                                                            <i className="bi bi-play-circle-fill" />
                                                        ) : (
                                                            <i className="bi bi-pause-circle-fill" />
                                                        )}{" "}
                                                        &nbsp;
                                                        {isCallInHold.current ? "Resume" : "Hold"}
                                                    </Button>
                                                ) : (
                                                    <></>
                                                )
                                            }

                                            <Button
                                                size="sm"
                                                className="btn btn-warning button"
                                                onClick={(e) => { showDndModal(locationInformation._id); stopNotificationRing("reset") }}
                                                disabled={isLoading || (notificationsSliceSelector.restartMotionStateLocationId.includes(locationInformation._id)) || (onlineLocations.dnd.includes(locationInformation._id)) || (!notificationData && !onlineLocations.online.includes(locationInformation._id) && !onlineLocations.dnd.includes(locationInformation._id)) || inCallState}
                                            >
                                                <i className="fa fa-ban" aria-hidden="true"></i>{" "} KIOSK - DO NOT DISTURB
                                            </Button>

                                        </div>

                                    </div>
                                </div>
                            }
                        </Card.Body >
                    </Card >
                    :
                    (notificationData && !locationInformation) ?
                        <Card
                            className={"card mb-2 " + cardClassName}
                            key={
                                notificationData
                                    ? notificationData.notificationId
                                    : ""
                            }
                        >
                            <Card.Body className="cardbody">
                                {
                                    <div className="location-details row d-flex align-items-center">
                                        <div className={"col-md-9"} >
                                            <div>
                                                <label className="d-inline-flex">

                                                    <span className="location-card-online">
                                                        {" "}
                                                    </span>

                                                    <h4 className="ml-2 text-uppercase">
                                                        {notificationData.agent_name}
                                                    </h4>
                                                    {
                                                        isCallInHold.current && <span className="ml-2 mt-1 text-danger">
                                                            <i className="bi bi-pause-circle-fill mr-1" />
                                                            <b>On hold</b>
                                                        </span>
                                                    }
                                                </label>
                                            </div>
                                            <div className="text-small">
                                                {notificationData.agent_email} ,{" "}
                                            </div>
                                            {notificationDate && (
                                                <div className="text-small mt-1">
                                                    {notificationDate}
                                                </div>
                                            )}
                                            <div className="dashboard-info-text mt-1">

                                            </div>
                                            <div className="button-group-info">
                                                <Button
                                                    size="sm"
                                                    className={
                                                        addAnimation
                                                            ? (notificationData.call_type === video_call_type ? "videoCallBtnColor " : "btn btn-success ") + "button notification-button-standards incoming-call-animation"
                                                            : (notificationData.call_type === video_call_type ? "videoCallBtnColor " : "btn btn-success ") + "button notification-button-standards"
                                                    }
                                                    onClick={(event) => {
                                                        joinCallFn(notificationData!);
                                                        setCallTriggeredFlag(true);
                                                    }}
                                                    disabled={
                                                        inCallState ||
                                                            inCallRef
                                                            ? true
                                                            : false ||
                                                            callTriggeredFlag
                                                    }
                                                >
                                                    {notificationData ? (
                                                        notificationData.call_type ===
                                                            voice_call_type ? (
                                                            <i className="bi bi-telephone-fill"></i>
                                                        ) : (
                                                            <i className="bi bi-camera-video-fill"></i>
                                                        )
                                                    ) : (
                                                        <></>
                                                    )}
                                                    &nbsp; ANSWER CALL
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    className="btn btn-danger button button-hangup-incoming notification-button-standards"
                                                    id={
                                                        notificationData &&
                                                            notificationData.call_type !==
                                                            voice_call_type
                                                            ? ""
                                                            : "button-hangup-incoming"
                                                    }
                                                    disabled={isLoading || !inCallState || isCallInHold.current || (isVideoCall && !isLocalParticipantExist)}
                                                    onClick={(e) => {
                                                        if (
                                                            notificationData?.call_type !==
                                                            voice_call_type
                                                        ) {
                                                            terminateVideoCall(
                                                                notificationData &&
                                                                    notificationData.notificationId
                                                                    ? notificationData.notificationId
                                                                    : "",
                                                                false,
                                                                notificationData &&
                                                                    notificationData.notificationId
                                                                    ? "no"
                                                                    : "yes"
                                                            );
                                                        }
                                                        if (
                                                            notificationData?.call_type ===
                                                            voice_call_type
                                                        ) {
                                                            dispatch(
                                                                terminateVoiceCall({
                                                                    callerId:
                                                                        notificationData?.call_sid!,
                                                                    disconnectCall:
                                                                        true,
                                                                })
                                                            );
                                                        }
                                                        if (notificationData?.call_type !== voice_call_type && notificationData?.call_type !== video_call_type) {
                                                            dispatch(terminateOutBoundVoiceCall());
                                                        }
                                                    }}
                                                >
                                                    END CALL
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    className="btn btn-dark button notification-button-standards"
                                                    onClick={(e) => { stopNotificationRing("mute-ringer"); setMuteRinger(true) }}
                                                    disabled={(!notificationsSliceSelector.playNotificationRinger || muteRinger)
                                                        ? true
                                                        : false}
                                                >
                                                    <i className="bi bi-bell-slash-fill" style={{ fontSize: '15px' }}></i> &nbsp; MUTE
                                                </Button>
                                                {
                                                    !isLoading &&
                                                        inCallState &&
                                                        (!notificationsSliceSelector.holdCallDetails ||
                                                            (notificationsSliceSelector.holdCallDetails &&
                                                                notificationsSliceSelector.holdCallDetails.inCallRefId &&
                                                                uniqueIdRef.current ===
                                                                notificationsSliceSelector.holdCallDetails.inCallRefId)) ? (
                                                        <Button
                                                            size="sm"
                                                            className="btn btn-warning button notification-button-standards"
                                                            onClick={() => callHoldCheck(voice_call_type)}
                                                            disabled={(isCallInHold.current) ? (inCallRef) ? true : false : (inCallRef === uniqueIdRef.current) ? false : true}
                                                        >
                                                            {isCallInHold.current ? (
                                                                <i className="bi bi-play-circle-fill" />
                                                            ) : (
                                                                <i className="bi bi-pause-circle-fill" />
                                                            )}{" "}
                                                            &nbsp;
                                                            {isCallInHold.current ? "Resume" : "Hold"}
                                                        </Button>
                                                    ) : (
                                                        <></>
                                                    )
                                                }
                                            </div>
                                        </div>
                                        {notificationData && notificationData.call_type && notificationData.call_type === voice_call_type ?
                                            (
                                                <div className="col-md-3 addressInfo">
                                                    {
                                                        (notificationData.third_agent) ?
                                                            <i className={(addAnimation) ? "bi bi bi-people with-incoming-call-animation" : "bi bi bi-people"}></i>
                                                            :
                                                            <i className={(addAnimation) ? "bi bi-telephone-inbound-fill with-incoming-call-animation" : "bi bi-telephone-inbound-fill"}></i>
                                                    }
                                                </div>
                                            ) : (
                                                <></>
                                            )
                                        }
                                    </div>
                                }
                            </Card.Body>
                        </Card>
                        :
                        (
                            <></>
                        )
            }
        </>
    );
};

export default LocationDetailsCard;
